// ─────────────────────────────────────────────────────────────────────────────
// HR Asset Allocation — data hooks (talks to /api/hr/assets/*)
//
// Devices are sourced from the accounts Fixed Asset register; allocations link
// a device to an employee. All endpoints are HR Admin / Superadmin only.
// ─────────────────────────────────────────────────────────────────────────────

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';

const BASE = '/api/hr/assets';

export interface AllocationDevice {
  _id: string;
  assetCode: string;
  name: string;
  category: string;
  brand: string;
  modelNumber: string;
  serialNumber: string;
  warranty: string;
  status: string;
  allocationStatus: 'available' | 'allocated';
  allocatedToName: string;
}

export interface AssetAllocation {
  _id: string;
  assetId: string;
  assetCode: string;
  assetName: string;
  category: string;
  brand: string;
  modelNumber: string;
  serialNumber: string;
  warranty: string;
  employeeUserId: string;
  employeeName: string;
  employeeDepartment: string;
  dateOfAssigning: string;
  status: 'allocated' | 'returned';
  returnedAt: string | null;
  createdAt: string;
}

export interface CreateAllocationPayload {
  assetId: string;
  employeeUserId: string;
  dateOfAssigning: string;
  brand?: string;
  modelNumber?: string;
  serialNumber?: string;
  warranty?: string;
  devicePassword?: string;
}

export const assetAllocationKeys = {
  devices: ['hr', 'asset-devices'] as const,
  allocations: ['hr', 'asset-allocations'] as const,
};

export function useAllocationDevices(enabled = true, availableOnly = false) {
  return useQuery({
    queryKey: [...assetAllocationKeys.devices, availableOnly],
    queryFn: async () =>
      (await api.get<AllocationDevice[]>(`${BASE}/devices${availableOnly ? '?available=1' : ''}`)).data,
    enabled,
  });
}

export function useAssetAllocations(enabled = true) {
  return useQuery({
    queryKey: assetAllocationKeys.allocations,
    queryFn: async () => (await api.get<AssetAllocation[]>(`${BASE}/allocations`)).data,
    enabled,
  });
}

export function useAssetAllocationMutations() {
  const queryClient = useQueryClient();
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: assetAllocationKeys.allocations });
    queryClient.invalidateQueries({ queryKey: assetAllocationKeys.devices });
  };

  const createAllocation = useMutation({
    mutationFn: async (payload: CreateAllocationPayload) =>
      (await api.post<AssetAllocation>(`${BASE}/allocations`, payload)).data,
    onSuccess: refresh,
  });

  const returnAllocation = useMutation({
    mutationFn: async (id: string) => (await api.post<{ ok: boolean }>(`${BASE}/allocations/${id}/return`)).data,
    onSuccess: refresh,
  });

  const revealPassword = useMutation({
    mutationFn: async (id: string) => (await api.get<{ password: string }>(`${BASE}/allocations/${id}/password`)).data,
  });

  return { createAllocation, returnAllocation, revealPassword };
}
